import { Link } from "react-router-dom";
import { DemoBadge } from "@/components/ui/DemoBadge";
import { cn } from "@/lib/utils";

interface ContentCardProps {
  href: string;
  title: string;
  description?: string;
  image?: string | null;
  meta?: string | string[];
  demo?: boolean;
  demoLabel?: string;
  className?: string;
  dark?: boolean;
}

export function ContentCard({
  href,
  title,
  description,
  image,
  meta,
  demo,
  demoLabel = "Demo listing",
  className,
  dark = false,
}: ContentCardProps) {
  const metaText = Array.isArray(meta) ? meta.filter(Boolean).join(" · ") : meta;

  return (
    <Link
      to={href}
      className={cn(
        "group flex flex-col overflow-hidden transition",
        dark
          ? "rounded-lg bg-white/5 ring-1 ring-white/10 hover:bg-white/10"
          : "rounded-lg border border-mist bg-white hover:shadow-md hover:shadow-ink/5",
        className,
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-mist">
        {image ? (
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-mist via-sand to-pine/15 px-4 text-center text-sm text-ridge/50">
            {title}
          </div>
        )}
        {demo ? (
          <div className="absolute start-3 top-3">
            <DemoBadge label={demoLabel} />
          </div>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <h3
          className={cn(
            "font-display text-base font-semibold leading-snug group-hover:text-pine",
            dark ? "text-sand" : "text-ink",
          )}
        >
          {title}
        </h3>
        {description ? (
          <p
            className={cn(
              "line-clamp-2 text-sm leading-relaxed",
              dark ? "text-mist/70" : "text-ridge/75",
            )}
          >
            {description}
          </p>
        ) : null}
        {metaText ? (
          <p
            className={cn(
              "mt-auto pt-1 text-xs font-medium",
              dark ? "text-mist/50" : "text-ridge/60",
            )}
          >
            {metaText}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
